<script setup lang="ts">
import {
  useSeoBase,
  useJsonLd,
  orgNode,
  localBusinessNode,
  breadcrumbNode,
} from "../composables/useSeo";
import { usePhoneNumber } from "../composables/usePhoneNumber";
import { useAnalytics } from "../composables/useAnalytics";

// Get phone number
const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();
const { trackConversion } = useAnalytics();

useSeoBase({
  title: "About Us | Professional Junk Removal in Minnesota | Junk Guys MN",
  description:
    "Learn about our professional junk removal services, commitment to responsible disposal, and serving Minnesota communities.",
  canonical: "https://junkguysmn.com/about",
});

useJsonLd([
  orgNode(),
  localBusinessNode(),
  breadcrumbNode([
    { name: "Home", url: "https://junkguysmn.com" },
    { name: "About", url: "https://junkguysmn.com/about" },
  ]),
]);
</script>

<template>
  <div>
    <!-- Hero Section -->
    <section
      class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white"
    >
      <div class="mx-auto max-w-7xl px-4 py-16 lg:py-24">
        <div class="text-center max-w-3xl mx-auto">
          <h1 class="text-4xl lg:text-6xl font-bold mb-6">
            About Junk Guys MN
          </h1>
          <p class="text-xl lg:text-2xl text-slate-200 leading-relaxed">
            Professional junk removal and demolition services serving the Twin
            Cities with transparency, reliability, and eco-friendly practices.
          </p>
        </div>
      </div>
    </section>

    <!-- Our Story Section -->
    <section class="bg-white">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-3xl font-bold text-slate-900 mb-6">Our Story</h2>
          <p class="text-lg text-slate-600 mb-4 leading-relaxed">
            Founded with a mission to provide reliable, responsible junk removal
            services, Junk Guys MN has been serving Minnesota communities for
            years. We understand that decluttering your space shouldn't be a
            hassle, and we're committed to making the process as smooth as
            possible.
          </p>
          <p class="text-lg text-slate-600 leading-relaxed">
            From residential cleanouts to commercial projects, we handle every
            job with the same level of professionalism and care.
          </p>
        </div>
      </div>
    </section>

    <!-- Our Mission Section -->
    <section class="bg-slate-50">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <h2 class="text-3xl font-bold text-slate-900 text-center mb-12">
          Our Mission
        </h2>
        <div class="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div
            class="bg-white p-8 rounded-lg shadow-md border border-slate-200"
          >
            <div class="flex items-center gap-4 mb-4">
              <div
                class="w-12 h-12 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0"
              >
                <svg
                  class="w-6 h-6 text-primary-main"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 class="text-xl font-bold text-slate-900">
                Professional Service
              </h3>
            </div>
            <p class="text-slate-600 leading-relaxed">
              We provide reliable, on-time service with a team of experienced
              professionals who treat your property with respect.
            </p>
          </div>
          <div
            class="bg-white p-8 rounded-lg shadow-md border border-slate-200"
          >
            <div class="flex items-center gap-4 mb-4">
              <div
                class="w-12 h-12 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0"
              >
                <svg
                  class="w-6 h-6 text-primary-main"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </div>
              <h3 class="text-xl font-bold text-slate-900">
                Responsible Disposal
              </h3>
            </div>
            <p class="text-slate-600 leading-relaxed">
              We're committed to environmental responsibility, donating usable
              items and recycling whenever possible.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us Section -->
    <section class="bg-white border-y border-slate-200">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <h2 class="text-3xl font-bold text-slate-900 text-center mb-3">
          Why Choose Junk Guys MN?
        </h2>
        <p class="text-lg text-slate-600 text-center mb-12">
          Locally owned and trusted throughout the Twin Cities
        </p>
        <div class="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
          <div
            class="text-center p-6 bg-slate-50 rounded-lg border border-slate-200"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 bg-primary-main/10 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-primary-main"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-slate-900 mb-2">
              Same-Day Service
            </h3>
            <p class="text-sm text-slate-600">
              Often available for urgent cleanout needs in Minneapolis and St.
              Paul
            </p>
          </div>
          <div
            class="text-center p-6 bg-slate-50 rounded-lg border border-slate-200"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 bg-primary-main/10 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-primary-main"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-slate-900 mb-2">
              Transparent Pricing
            </h3>
            <p class="text-sm text-slate-600">
              No hidden fees, volume-based pricing with upfront estimates
            </p>
          </div>
          <div
            class="text-center p-6 bg-slate-50 rounded-lg border border-slate-200"
          >
            <div
              class="w-16 h-16 mx-auto mb-4 bg-primary-main/10 rounded-full flex items-center justify-center"
            >
              <svg
                class="w-8 h-8 text-primary-main"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>
            <h3 class="text-lg font-bold text-slate-900 mb-2">Eco-Friendly</h3>
            <p class="text-sm text-slate-600">
              Donation and recycling programs for responsible disposal
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Complete Services Section -->
    <section class="bg-slate-50">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <h2 class="text-3xl font-bold text-slate-900 text-center mb-3">
          Complete Junk Removal & Demolition Services
        </h2>
        <p class="text-center text-lg text-slate-600 mb-12">
          Professional hauling and teardown across the Twin Cities
        </p>

        <div class="grid gap-6 grid-cols-2 lg:grid-cols-4">
          <!-- Junk Removal -->
          <div
            class="p-6 bg-white rounded-lg shadow-md border border-slate-200 hover:border-primary-main transition-colors"
          >
            <h3 class="text-lg font-bold text-slate-900 mb-2">Junk Removal</h3>
            <p class="text-sm text-slate-600 mb-3">
              Full-service hauling for any household or business junk
            </p>
            <NuxtLink
              to="/services/junk-removal"
              class="text-sm font-semibold text-primary-main hover:text-primary-dark"
              >Learn More →</NuxtLink
            >
          </div>

          <!-- Appliance Removal -->
          <div
            class="p-6 bg-white rounded-lg shadow-md border border-slate-200 hover:border-primary-main transition-colors"
          >
            <h3 class="text-lg font-bold text-slate-900 mb-2">
              Appliance Removal
            </h3>
            <p class="text-sm text-slate-600 mb-3">
              Safe disposal of refrigerators, washers, dryers, and more
            </p>
            <NuxtLink
              to="/services/appliance-removal"
              class="text-sm font-semibold text-primary-main hover:text-primary-dark"
              >Learn More →</NuxtLink
            >
          </div>

          <!-- Furniture Removal -->
          <div
            class="p-6 bg-white rounded-lg shadow-md border border-slate-200 hover:border-primary-main transition-colors"
          >
            <h3 class="text-lg font-bold text-slate-900 mb-2">
              Furniture Removal
            </h3>
            <p class="text-sm text-slate-600 mb-3">
              Couches, mattresses, tables - we haul it all away
            </p>
            <NuxtLink
              to="/services/furniture-removal"
              class="text-sm font-semibold text-primary-main hover:text-primary-dark"
              >Learn More →</NuxtLink
            >
          </div>

          <!-- Construction Debris -->
          <div
            class="p-6 bg-white rounded-lg shadow-md border border-slate-200 hover:border-primary-main transition-colors"
          >
            <h3 class="text-lg font-bold text-slate-900 mb-2">
              Construction Debris
            </h3>
            <p class="text-sm text-slate-600 mb-3">
              Drywall, flooring, lumber, and renovation waste removal
            </p>
            <NuxtLink
              to="/services/construction-debris"
              class="text-sm font-semibold text-primary-main hover:text-primary-dark"
              >Learn More →</NuxtLink
            >
          </div>

          <!-- Garage Cleanout -->
          <div
            class="p-6 bg-white rounded-lg shadow-md border border-slate-200 hover:border-primary-main transition-colors"
          >
            <h3 class="text-lg font-bold text-slate-900 mb-2">
              Garage Cleanout
            </h3>
            <p class="text-sm text-slate-600 mb-3">
              Complete garage clearing with donation and recycling options
            </p>
            <NuxtLink
              to="/services/garage-cleanout"
              class="text-sm font-semibold text-primary-main hover:text-primary-dark"
              >Learn More →</NuxtLink
            >
          </div>

          <!-- Hot Tub Removal -->
          <div
            class="p-6 bg-white rounded-lg shadow-md border border-slate-200 hover:border-primary-main transition-colors"
          >
            <h3 class="text-lg font-bold text-slate-900 mb-2">
              Hot Tub Removal
            </h3>
            <p class="text-sm text-slate-600 mb-3">
              Expert hot tub demolition and responsible disposal
            </p>
            <NuxtLink
              to="/services/hot-tub-removal"
              class="text-sm font-semibold text-primary-main hover:text-primary-dark"
              >Learn More →</NuxtLink
            >
          </div>

          <!-- Shed Demolition -->
          <div
            class="p-6 bg-white rounded-lg shadow-md border border-slate-200 hover:border-primary-main transition-colors"
          >
            <h3 class="text-lg font-bold text-slate-900 mb-2">
              Shed Demolition
            </h3>
            <p class="text-sm text-slate-600 mb-3">
              Full teardown and debris removal for any shed size
            </p>
            <NuxtLink
              to="/services/shed-demolition"
              class="text-sm font-semibold text-primary-main hover:text-primary-dark"
              >Learn More →</NuxtLink
            >
          </div>

          <!-- Deck Removal -->
          <div
            class="p-6 bg-white rounded-lg shadow-md border border-slate-200 hover:border-primary-main transition-colors"
          >
            <h3 class="text-lg font-bold text-slate-900 mb-2">Deck Removal</h3>
            <p class="text-sm text-slate-600 mb-3">
              Licensed deck demolition with complete site cleanup
            </p>
            <NuxtLink
              to="/services/deck-removal"
              class="text-sm font-semibold text-primary-main hover:text-primary-dark"
              >Learn More →</NuxtLink
            >
          </div>
        </div>
      </div>
    </section>

    <!-- Service Areas Section -->
    <section class="bg-white">
      <div class="mx-auto max-w-7xl px-4 py-14">
        <h2 class="text-3xl font-bold text-slate-900 mb-6 text-center">
          Service Areas
        </h2>
        <p class="text-lg text-slate-600 text-center mb-8 max-w-3xl mx-auto">
          We proudly serve the greater Minnesota Twin Cities area with same-day
          availability in most locations.
        </p>
        <div class="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <div class="bg-slate-50 p-6 rounded-lg border border-slate-200">
            <h3 class="text-xl font-bold text-slate-900 mb-4">
              Core Service Cities
            </h3>
            <ul class="space-y-2">
              <li class="flex items-center text-slate-700">
                <svg
                  class="w-5 h-5 text-primary-main mr-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                Minneapolis
              </li>
              <li class="flex items-center text-slate-700">
                <svg
                  class="w-5 h-5 text-primary-main mr-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                Saint Paul
              </li>
              <li class="flex items-center text-slate-700">
                <svg
                  class="w-5 h-5 text-primary-main mr-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                Bloomington
              </li>
              <li class="flex items-center text-slate-700">
                <svg
                  class="w-5 h-5 text-primary-main mr-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                Eden Prairie
              </li>
              <li class="flex items-center text-slate-700">
                <svg
                  class="w-5 h-5 text-primary-main mr-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                Edina
              </li>
            </ul>
          </div>
          <div class="bg-slate-50 p-6 rounded-lg border border-slate-200">
            <h3 class="text-xl font-bold text-slate-900 mb-4">
              Extended Coverage
            </h3>
            <ul class="space-y-2">
              <li class="flex items-center text-slate-700">
                <svg
                  class="w-5 h-5 text-primary-main mr-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                Richfield
              </li>
              <li class="flex items-center text-slate-700">
                <svg
                  class="w-5 h-5 text-primary-main mr-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                Maple Grove
              </li>
              <li class="flex items-center text-slate-700">
                <svg
                  class="w-5 h-5 text-primary-main mr-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                Plymouth
              </li>
              <li class="flex items-center text-slate-700">
                <svg
                  class="w-5 h-5 text-primary-main mr-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                Woodbury
              </li>
              <li class="flex items-center text-slate-700">
                <svg
                  class="w-5 h-5 text-primary-main mr-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                And many more!
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="bg-primary-main text-white">
      <div class="mx-auto max-w-7xl px-4 py-16 text-center">
        <h2 class="text-3xl lg:text-4xl font-bold mb-4">
          Ready to Get Started?
        </h2>
        <p class="text-xl text-white mb-8">
          Contact us today for a free estimate or book your appointment online.
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <a
            :href="phoneNumberTel"
            @click="trackConversion"
            class="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-main font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            📞 Call for Estimate
          </a>
          <NuxtLink
            to="/book"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-main transition-all duration-200"
          >
            📅 Book Online
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
